/**
 * Pagination Component
 *
 * Reusable pagination controls for admin pages
 * Shows page numbers with ellipsis for large page counts
 *
 * Epic: Super Admin Catalog Management Console
 * Story: SCRUM-299
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Get visible page numbers with ellipsis
 * Shows max 7 page numbers: [1] ... [4] [5] [6] ... [10]
 */
function getVisiblePages(currentPage, totalPages) {
  if (totalPages <= 7) {
    // Show all pages if 7 or fewer
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = [];

  // Always show first page
  pages.push(1);

  if (currentPage <= 3) {
    // Near start: [1] [2] [3] [4] ... [10]
    pages.push(2, 3, 4, '...', totalPages);
  } else if (currentPage >= totalPages - 2) {
    // Near end: [1] ... [7] [8] [9] [10]
    pages.push('...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
  } else {
    // Middle: [1] ... [4] [5] [6] ... [10]
    pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
  }

  return pages;
}

export default function Pagination({ currentPage, totalPages, onPageChange }) {
  // Guard: no pagination for 0/1/NaN/undefined pages or invalid currentPage
  if (!totalPages || !Number.isFinite(totalPages) || totalPages <= 1) {
    return null;
  }
  const safeCurrent = Number.isFinite(currentPage) ? currentPage : 1;

  const visiblePages = getVisiblePages(safeCurrent, totalPages);

  return (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      {/* Mobile View */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => onPageChange(safeCurrent - 1)}
          disabled={safeCurrent === 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(safeCurrent + 1)}
          disabled={safeCurrent === totalPages}
          className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>

      {/* Desktop View */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Page <span className="font-medium">{safeCurrent}</span> of{' '}
            <span className="font-medium">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            {/* Previous Button */}
            <button
              onClick={() => onPageChange(safeCurrent - 1)}
              disabled={safeCurrent === 1}
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">Previous</span>
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>

            {/* Page Numbers */}
            {visiblePages.map((page, index) => {
              if (page === '...') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                  >
                    ...
                  </span>
                );
              }

              const isCurrentPage = page === safeCurrent;

              return (
                <button
                  key={page}
                  onClick={() => onPageChange(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                    isCurrentPage
                      ? 'z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                  }`}
                >
                  {page}
                </button>
              );
            })}

            {/* Next Button */}
            <button
              onClick={() => onPageChange(safeCurrent + 1)}
              disabled={safeCurrent === totalPages}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span className="sr-only">Next</span>
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}
